import type { BackgroundElement } from '../api/types';
import ColorPickerField from './ColorPickerField';

interface Props {
  value: BackgroundElement;
  onChange: (next: BackgroundElement) => void;
}

const SOURCES: Array<[BackgroundElement['type'], string]> = [
  ['image', 'Image'],
  ['color', 'Color'],
];

const STYLES: Array<[BackgroundElement['effect']['style'], string]> = [
  ['radial', 'Radial (spotlight)'],
  ['linear', 'Linear'],
];

const DIRECTIONS: Array<[BackgroundElement['effect']['direction'], string]> = [
  ['vertical', 'Vertical'],
  ['horizontal', 'Horizontal'],
  ['diagonal', 'Diagonal'],
];

const EYEBROW = 'block font-mono text-[10px] uppercase tracking-widish text-ff-mute';

/** Every control here wraps a NATIVE input kept in the accessibility tree with
 *  `sr-only` rather than a div wearing an ARIA role. Checked state, keyboard
 *  operation and label association all keep working for free; only the paint is
 *  ours. `peer-focus-visible` is what puts the focus ring back on the pill the
 *  hidden input stands for. */
export function BackgroundEditor({ value, onChange }: Props) {
  const set = (patch: Partial<BackgroundElement>) => onChange({ ...value, ...patch });
  const setEffect = (patch: Partial<BackgroundElement['effect']>) =>
    onChange({ ...value, effect: { ...value.effect, ...patch } });

  const on = value.effect.enabled;
  const intensityPct = Math.round(value.effect.intensity * 100);

  return (
    <div className="space-y-3">
      <div role="radiogroup" aria-label="Background source" className="flex gap-1.5">
        {SOURCES.map(([t, label]) => (
          <label key={t} className="flex-1 cursor-pointer">
            <input
              type="radio"
              name="bg-src"
              className="peer sr-only"
              aria-label={label}
              checked={value.type === t}
              onChange={() =>
                set(t === 'color' ? { type: 'color', color: value.color ?? '#627873' } : { type: t })
              }
            />
            <span
              className={`block rounded-full px-3 py-1.5 text-center text-[11px] font-medium transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-ff-forest/30 ${
                value.type === t
                  ? 'bg-ff-forest text-ff-ivory'
                  : 'bg-ff-mint/60 text-ff-forest hover:bg-ff-mint'
              }`}
            >
              {label}
            </span>
          </label>
        ))}
      </div>

      {value.type === 'color' && (
        <ColorPickerField
          label="Background color"
          value={value.color ?? '#627873'}
          onChange={(color) => set({ color })}
        />
      )}

      <label className="flex cursor-pointer items-center justify-between gap-3">
        <span className="text-[13px] text-ff-ink">Gradient effect</span>
        <input
          type="checkbox"
          className="peer sr-only"
          aria-label="Gradient effect"
          checked={on}
          onChange={(e) => setEffect({ enabled: e.target.checked })}
        />
        <span
          className={`relative h-5 w-9 shrink-0 rounded-full transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-ff-forest/30 ${
            on ? 'bg-ff-forest' : 'bg-ff-sand'
          }`}
        >
          <span
            className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-soft transition-transform motion-reduce:transition-none ${
              on ? 'translate-x-[18px]' : 'translate-x-0.5'
            }`}
          />
        </span>
      </label>

      {on && (
        <div className="space-y-3 rounded-xl border border-ff-sand/70 bg-ff-ivory/70 p-3">
          <div className="space-y-1.5">
            <label htmlFor="bg-effect-style" className={EYEBROW}>
              Style
            </label>
            <select
              id="bg-effect-style"
              aria-label="Effect style"
              value={value.effect.style}
              onChange={(e) =>
                setEffect({ style: e.target.value as BackgroundElement['effect']['style'] })
              }
              className="ff-input py-2 text-sm"
            >
              {STYLES.map(([s, label]) => (
                <option key={s} value={s}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          {/* A radial spotlight has no direction to point, so the control only
              exists once it can mean something. */}
          {value.effect.style === 'linear' && (
            <div className="space-y-1.5">
              <label htmlFor="bg-effect-direction" className={EYEBROW}>
                Direction
              </label>
              <select
                id="bg-effect-direction"
                aria-label="Effect direction"
                value={value.effect.direction}
                onChange={(e) =>
                  setEffect({
                    direction: e.target.value as BackgroundElement['effect']['direction'],
                  })
                }
                className="ff-input py-2 text-sm"
              >
                {DIRECTIONS.map(([d, label]) => (
                  <option key={d} value={d}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="space-y-1.5">
            <div className="flex items-baseline justify-between">
              <label htmlFor="bg-effect-intensity" className={EYEBROW}>
                Intensity
              </label>
              <span className="font-mono text-[10px] tabular-nums text-ff-mute">
                {intensityPct}%
              </span>
            </div>
            <input
              id="bg-effect-intensity"
              type="range"
              aria-label="Effect intensity"
              min={0}
              max={100}
              value={intensityPct}
              onChange={(e) => setEffect({ intensity: Number(e.target.value) / 100 })}
              className="ff-slider w-full"
            />
          </div>
        </div>
      )}
    </div>
  );
}
